// Privacy filter for everything Vaartix sends to Sentry, from the backend and the dashboard.
//
// Vaartix handles other people's email, so this is an allowlist: each event is rebuilt from a few
// fields known to be safe (error type, message and stack, HTTP method, route pattern, status,
// release, environment, service), and anything else (request headers, cookies, bodies, query
// strings, user data, extra context, local variables) never leaves the process. Text that does go
// (error messages, breadcrumb messages, URLs) is passed through redact(), which removes email
// addresses, API keys, webhook secrets, bearer tokens, long tokens and login codes.
//
// No dependencies, so the dashboard can import it as well: `@vaartix/shared/scrub`.

type Json = Record<string, unknown>;

/**
 * Sentry's `dataCollection` option (v11 replaced `sendDefaultPii` with it), with every category
 * off: no user info, cookies, headers, HTTP bodies, query parameters, database values, queue
 * arguments or local variables. scrubEvent() below is the second line of defence.
 */
export const NO_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
} as const;

const PATTERNS: [RegExp, string | ((match: string) => string)][] = [
  [/\bBearer\s+\S+/gi, 'Bearer [redacted]'],
  [/\bwhsec_[A-Za-z0-9+/=_-]+/g, '[webhook secret]'],
  [/\bvx_[A-Za-z0-9_-]+/g, '[api key]'],
  [/\brzp_(?:live|test)_[A-Za-z0-9]+/g, '[api key]'],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
  // Turnstile tokens, session tokens, signatures, base64 content: a long run of token characters
  // (dots included, as in `0.abc….def`) with a long part that mixes letters and digits. Host
  // names (`challenges.cloudflare.com`) and Vaartix IDs (`em_7hq2kv9xr4m1c8d3`) don't qualify.
  [/[A-Za-z0-9_\-+=.]{24,}/g, (run) => (run.split('.').some(looksRandom) ? '[token]' : run)],
  // Login codes are 6 digits.
  [/(?<![\d.])\d{6}(?![\d.])/g, '[code]'],
];

function looksRandom(part: string): boolean {
  return part.length >= 16 && /[A-Za-z]/.test(part) && /\d/.test(part);
}

/** Remove secrets and personal data from a piece of text. */
export function redact(text: string): string {
  let out = text;
  // A string or a function: both are valid replace() arguments.
  for (const [pattern, replacement] of PATTERNS) out = out.replace(pattern, replacement as string);
  return out;
}

/** The path of a URL, without its query string or fragment, redacted. */
export function redactUrl(url: string): string {
  const path = url.split(/[?#]/, 1)[0] ?? '';
  return redact(path);
}

const isObject = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown) => (typeof v === 'string' ? v : undefined);
const num = (v: unknown) => (typeof v === 'number' ? v : undefined);

/** Copy only the listed keys whose values are present. */
function pick(source: Json, keys: readonly string[]): Json {
  const out: Json = {};
  for (const key of keys) if (source[key] !== undefined) out[key] = source[key];
  return out;
}

// Stack frames: where the code is, never the values it had (no `vars`).
const FRAME_KEYS = [
  'filename',
  'abs_path',
  'module',
  'function',
  'lineno',
  'colno',
  'in_app',
  'platform',
  'pre_context',
  'context_line',
  'post_context',
] as const;

function scrubException(value: unknown): Json | undefined {
  if (!isObject(value)) return undefined;
  const out = pick(value, ['type', 'module', 'thread_id']);
  if (str(value.value) !== undefined) out.value = redact(value.value as string);
  if (isObject(value.mechanism))
    out.mechanism = pick(value.mechanism, ['type', 'handled', 'synthetic']);
  const frames = isObject(value.stacktrace) ? value.stacktrace.frames : undefined;
  if (Array.isArray(frames)) {
    out.stacktrace = { frames: frames.filter(isObject).map((frame) => pick(frame, FRAME_KEYS)) };
  }
  return out;
}

// Contexts describe the machine and the runtime, not the user, except these.
const SAFE_CONTEXTS = ['os', 'runtime', 'browser', 'device', 'app', 'culture', 'trace'] as const;
const CONTEXT_KEYS: Record<string, readonly string[]> = {
  os: ['name', 'version', 'kernel_version'],
  runtime: ['name', 'version'],
  browser: ['name', 'version'],
  device: ['family', 'model', 'arch', 'memory_size', 'boot_time'],
  app: ['app_start_time', 'app_memory'],
  culture: ['locale', 'timezone'],
  trace: ['trace_id', 'span_id', 'parent_span_id', 'op', 'status'],
};

function scrubTags(tags: unknown): Record<string, string | number | boolean> | undefined {
  if (!isObject(tags)) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(tags)) {
    if (typeof value === 'string') out[key] = redact(value);
    else if (typeof value === 'number' || typeof value === 'boolean') out[key] = value;
  }
  return out;
}

/**
 * The event Sentry may send: error type, message and stack, the request's method and route (no
 * headers, cookies, body or query string), tags, release, environment and scrubbed breadcrumbs.
 * Use as `beforeSend`.
 */
export function scrubEvent<T>(event: T): T {
  const e = event as unknown as Json;
  const out = pick(e, [
    'event_id',
    'timestamp',
    'start_timestamp',
    'platform',
    'level',
    'logger',
    'release',
    'dist',
    'environment',
    'server_name',
    'sdk',
    'type',
    'fingerprint',
    'debug_meta',
  ]);
  if (str(e.transaction) !== undefined) out.transaction = redactUrl(e.transaction as string);
  if (str(e.message) !== undefined) out.message = redact(e.message as string);
  if (isObject(e.logentry) && str(e.logentry.message) !== undefined) {
    // Parameters can be anything, so only the (redacted) format string goes.
    out.logentry = { message: redact(e.logentry.message as string) };
  }
  const values = isObject(e.exception) ? e.exception.values : undefined;
  if (Array.isArray(values)) {
    out.exception = { values: values.map(scrubException).filter((v) => v !== undefined) };
  }
  if (isObject(e.request)) {
    const request: Json = {};
    if (str(e.request.method)) request.method = e.request.method;
    if (str(e.request.url)) request.url = redactUrl(e.request.url as string);
    out.request = request;
  }
  const tags = scrubTags(e.tags);
  if (tags) out.tags = tags;
  if (isObject(e.contexts)) {
    const contexts: Json = {};
    for (const name of SAFE_CONTEXTS) {
      const context = e.contexts[name];
      if (isObject(context)) contexts[name] = pick(context, CONTEXT_KEYS[name] ?? []);
    }
    out.contexts = contexts;
  }
  if (Array.isArray(e.breadcrumbs)) {
    out.breadcrumbs = e.breadcrumbs
      .map((b) => scrubBreadcrumb(b as Json))
      .filter((b): b is Json => b !== null);
  }
  return out as unknown as T;
}

/**
 * The breadcrumb Sentry may keep: what kind of thing happened, when, and for HTTP requests and
 * page changes the method, path (no query string) and status. Use as `beforeBreadcrumb`.
 */
export function scrubBreadcrumb<T>(breadcrumb: T): T | null {
  if (!isObject(breadcrumb)) return null;
  const b = breadcrumb as Json;
  const out = pick(b, ['type', 'category', 'level', 'timestamp']);
  if (str(b.message) !== undefined) out.message = redact(b.message as string);
  if (isObject(b.data)) {
    const data: Json = {};
    if (str(b.data.method)) data.method = b.data.method;
    if (num(b.data.status_code) !== undefined) data.status_code = b.data.status_code;
    for (const key of ['url', 'from', 'to'] as const) {
      const value = str(b.data[key]);
      if (value !== undefined) data[key] = redactUrl(value);
    }
    if (Object.keys(data).length) out.data = data;
  }
  return out as unknown as T;
}
